import {createSelector} from '@reduxjs/toolkit'
import {selectGuestInfo} from './slices/guestInfoSlice'
import {selectCartItems} from './slices/shoppingCartSlice'
import {
  BookingReservation,
  CreateIbeBookingMetadata,
  ShoppingCartItem
} from '../types'
import {
  calcCartPriceSummary,
  calcOfferPriceSummary
} from '../services/offerUtils'
import {selectSessionId} from './slices/sessionSlice'

/**
 * Creates a formatted comment for the reservation
 * @param sessionId - The session ID for the IBE booking
 * @returns
 */
function formatReservationComment(sessionId: string | null): string {
  const source = 'Source: New IBE'
  const session = `IbeSessionId: ${sessionId || 'Unknown'}`
  return [source, session].join(' | ')
}

/**
 * Selects the booking metadata for the IBE booking.
 */
export const selectBookingMetadata = createSelector(
  [selectGuestInfo, selectCartItems, selectSessionId],
  (guestInfo, cartItems, sessionId): CreateIbeBookingMetadata => {
    const reservations: BookingReservation[] = cartItems.map(
      (cartItem: ShoppingCartItem) => {
        const {offer, additionalServices, guests} = cartItem
        const {promoCode, corporateCode} = offer
        const timeSlices = offer.timeSlices.map(_ => ({
          ratePlanId: offer.ratePlan?.id
        }))

        const services = additionalServices.map(service => ({
          serviceId: service.service?.id
        }))

        const priceSummary = calcOfferPriceSummary(offer, additionalServices)

        return {
          arrival: offer.arrival,
          departure: offer.departure,
          adults: guests,
          childrenAges: [],
          guestComment: guestInfo.comments,
          comment: formatReservationComment(sessionId),
          channelCode: 'Ibe',
          primaryGuest: {
            firstName: guestInfo.firstName,
            lastName: guestInfo.lastName,
            email: guestInfo.email,
            phone: guestInfo.phone
          },
          guaranteeType: offer.minGuaranteeType,
          timeSlices,
          services,
          prePaymentAmount: {
            amount: priceSummary.totalPrepayment,
            currency: offer.prePaymentAmount.currency
          },
          ...(promoCode && {promoCode}),
          ...(!promoCode && corporateCode && {corporateCode})
        }
      }
    )

    const cartPriceSummary = calcCartPriceSummary(cartItems)

    return {
      type: 'CreateIbeBooking',
      ibeSessionId: sessionId || 'unknown',
      booker: {
        firstName: guestInfo.firstName,
        lastName: guestInfo.lastName,
        email: guestInfo.email,
        phone: guestInfo.phone
      },
      reservations,
      totalPrepayment: {
        amount: cartPriceSummary.totalPrepayment,
        currency: 'USD'
      },
      total: {
        amount: cartPriceSummary.total,
        currency: 'USD'
      }
    }
  }
)
